import React, { useEffect, useState, useRef } from 'react';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  ScatterController,
} from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import _ from 'lodash';

ChartJS.register(  LineController, ScatterController, LineElement, PointElement, LinearScale, Title, Tooltip, Legend, Filler);

const BellCurveGraph = ({ list = [], studentMark = NaN }) => {
  const [userType, setUserType] = useState(1);
  const chartRef = useRef(null);

  useEffect(() => {
    const type = localStorage.getItem('userType');
    setUserType(type);
  }, []);

  const getStudentPercentile = (data, mark) => {
    const sorted = _.sortBy(data.map(Number));
    const rank = sorted.filter(score => score < mark).length;
    return (rank / sorted.length) * 100;
  };

  const calculatePercentiles = (data, studentMark) => {
    const numericMarks = data.map(Number);

    // Ensure student's score is included in curve
    const allMarks = [...new Set([...numericMarks, studentMark])].sort((a, b) => a - b);

    return allMarks.map(mark => ({
      x: mark,
      y: getStudentPercentile(numericMarks, mark),
    }));
  };

  const chartData = (marks, studentMark) => {
    const numericMarks = _.map(marks, (i) => parseFloat(i));
    const sorted = _.sortBy(numericMarks);
    const total = sorted.length;
    const min = _.min(numericMarks);
    const max = _.max(numericMarks);

    const studentPercentile = getStudentPercentile(numericMarks, studentMark);
    const percentilePoints = calculatePercentiles(numericMarks, studentMark);

    const datasets = [
      {
        label: 'Percentile Curve',
        data: percentilePoints,
        type: 'line',
        borderColor: 'rgba(54, 162, 235, 0.6)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderDash: [5, 5],
        pointRadius: 2,
        tension: 0.4,
        fill: {
          target: 'origin',
          above: 'rgba(54, 162, 235, 0.2)',
        },
        showLine: true,
        spanGaps: true,
      },
      {
        label: `Student Score (${studentMark})`,
        data: [
          { x: studentMark, y: 0 },
          { x: studentMark, y: studentPercentile },
        ],
        borderColor: 'green',
        borderWidth: 4,
        pointRadius: 0,
        type: 'line',
        showLine: true,
      },
      {
        label: `Student Percentile (${Math.round(studentPercentile)}%)`,
        data: [
          { x: min, y: studentPercentile },
          { x: studentMark, y: studentPercentile },
        ],
        borderColor: 'green',
        borderWidth: 2,
        pointRadius: 0,
        type: 'line',
        showLine: true,
      },
      {
        label: 'Max Marks Scored',
        data: [
          { x: max, y: 0 },
          { x: max, y: 100 },
        ],
        borderColor: 'black',
        borderDash: [4, 4],
        borderWidth: 2,
        pointRadius: 0,
        type: 'line',
        showLine: true,
      },
      {
        label: 'Minimum Marks Scored',
        data: [
          { x: min, y: 0 },
          { x: min, y: 100 },
        ],
        borderColor: 'red',
        borderDash: [4, 4],
        borderWidth: 2,
        pointRadius: 0,
        type: 'line',
        showLine: true,
      },
      {
        label: `Score (${studentMark},${Math.round(studentPercentile)}%)`,
        data: [{ x: studentMark, y: studentPercentile }],
        backgroundColor: '#fc6565',
        pointRadius: 5,
        type: 'scatter',
      },
    ];

    return { datasets };
  };

  const options = {
    scales: {
      x: {
        title: { display: true, text: 'Marks' },
        min: 0,
        max: 100,
      },
      y: {
        title: { display: true, text: 'Percentile (%)' },
        min: 0,
        max: 100,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || '';
            const x = context.parsed.x;
            const y = context.parsed.y;

            if (datasetLabel.includes("Score")) {
              return `Your score: ${x}, Percentile: ${Math.round(y)}%`;
            }
            if (datasetLabel.includes("Student Score")) {
              return `Your score: ${x}`;
            }
            if (datasetLabel.includes("Student Percentile")) {
              return `Your percentile: ${Math.round(y)}%`;
            }
            if (datasetLabel === 'Max Marks Scored') {
              return `Top scorer got: ${x} marks`;
            }
            if (datasetLabel === 'Minimum Marks Scored') {
              return `Lowest scorer got: ${x} marks`;
            }
            if (datasetLabel === 'Percentile Curve') {
              return `Score: ${x}, Percentile: ${Math.round(y)}%`;
            }

            return `(${x}, ${y})`;
          }
        }
      }
    },
    maintainAspectRatio: false,
  };

  return (
  
      <Scatter ref={chartRef} data={chartData(list, studentMark)} options={options} />

  );
};

export default BellCurveGraph;